import { useState } from 'react';
import type { CSSProperties } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useTeamInfo, useTeamMembers, createInvite } from './api';

// Empty state de la bandeja por defecto. Si estás prácticamente solo en el
// equipo y puedes invitar, anima a hacerlo (enlace + email) con tono informal.
// En cualquier otro caso, un mensaje vacío amable.
export default function InboxEmpty() {
  const { profile } = useAuth();
  const { team } = useTeamInfo();
  const { members } = useTeamMembers();

  const myRole = members.find((m) => m.id === profile?.id)?.role;
  const canInvite = myRole === 'owner' || myRole === 'admin';
  const aloneish = members.length <= 3;

  const [email, setEmail] = useState('');
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const [error, setError] = useState('');

  if (!team || !canInvite || !aloneish) {
    return (
      <div style={styles.plain}>
        <p style={styles.plainText}>
          Aún no tienes hilos. Cuando alguien te asigne uno, te mencione o te añada como seguidor —o crees uno tú— aparecerá aquí.
        </p>
      </div>
    );
  }

  const link = `${window.location.origin}/join/${team.id}/${team.join_token}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* copia manual */ }
  };

  const invite = async () => {
    if (!email.includes('@')) { setError('Pon un email válido'); return; }
    setBusy(true); setError('');
    try {
      await createInvite(email);
      setSent(email);
      setEmail('');
      setTimeout(() => setSent(null), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo enviar');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={styles.card}>
      <div style={styles.iconBadge}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="9" cy="8" r="3.5" />
          <path d="M3 20v-1a5 5 0 0 1 5-5h2a5 5 0 0 1 5 5v1" />
          <path d="M18 7.5v5" /><path d="M15.5 10h5" />
        </svg>
      </div>
      <h2 style={styles.title}>Esto está un poco solo…</h2>
      <p style={styles.subtitle}>
        SimplyTeam se disfruta en equipo. Invita a tus compañeros y empezad a mover hilos juntos.
      </p>

      <div style={styles.block}>
        <span style={styles.label}>Comparte el enlace</span>
        <div style={styles.linkRow}>
          <input readOnly value={link} style={styles.input} onFocus={(e) => e.currentTarget.select()} />
          <button type="button" onClick={copy} style={styles.btnDark}>{copied ? '¡Copiado!' : 'Copiar'}</button>
        </div>
      </div>

      <div style={styles.block}>
        <span style={styles.label}>O invita por email</span>
        <div style={styles.linkRow}>
          <input
            type="email"
            placeholder="compañero@empresa.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') invite(); }}
            style={styles.input}
          />
          <button type="button" onClick={invite} disabled={busy} style={styles.btnPrimary}>
            {busy ? 'Enviando…' : 'Enviar'}
          </button>
        </div>
        {sent && <p style={styles.ok}>Invitación enviada a {sent} ✓</p>}
        {error && <p style={styles.error}>{error}</p>}
      </div>

      <p style={styles.hint}>Puedes gestionar todo esto luego en Configuración → Invitaciones.</p>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  plain: { marginTop: 24 },
  plainText: { color: '#94a3b8', fontSize: 14, lineHeight: 1.5, maxWidth: 520 },
  card: {
    marginTop: 12,
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 16,
    padding: 28,
    maxWidth: 560,
    boxShadow: '0 1px 3px rgba(15,23,42,0.05)',
  },
  iconBadge: {
    width: 48, height: 48, borderRadius: 12, background: '#eef2ff', color: '#4338ca',
    display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14,
  },
  title: { fontSize: 20, fontWeight: 800, color: '#0f172a', margin: '0 0 6px', letterSpacing: '-0.3px' },
  subtitle: { color: '#64748b', fontSize: 14.5, lineHeight: 1.55, margin: '0 0 20px' },
  block: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 },
  label: { fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 },
  linkRow: { display: 'flex', gap: 8, alignItems: 'center' },
  input: {
    flex: 1, minWidth: 0, border: '1px solid #cbd5e1', borderRadius: 8, padding: '10px 12px',
    fontSize: 14, outline: 'none', background: '#fff', color: '#0f172a',
  },
  btnPrimary: {
    background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 16px',
    fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
  },
  btnDark: {
    background: '#0f172a', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 16px',
    fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
  },
  ok: { color: '#16a34a', fontSize: 13, margin: '8px 0 0' },
  error: { color: '#dc2626', fontSize: 13, margin: '8px 0 0' },
  hint: { color: '#94a3b8', fontSize: 12.5, margin: '4px 0 0' },
};
